'use client';

import { useEffect } from 'react';
import { useImmer } from 'use-immer';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { useLowHealthWarnings } from 'src/hooks/useLowHealthWarning';

// Warning threshold for low health
const LOW_HEALTH_THRESHOLD_PERCENT = 20;

// Rate limit warnings per entity to once every 30 seconds
const WARNING_RATE_LIMIT_MS = 30 * 1000;

/**
 * Fires warning toasts when account health drops below the threshold (20%)
 * for both cross accounts and isolated positions across all subaccounts.
 */
const useLiquidationWarningToasts = () => {
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const accountsMap = useDriftAccountStore((s) => s.accounts);

	// Get all low-health warnings across all subaccounts
	const warnings = useLowHealthWarnings(LOW_HEALTH_THRESHOLD_PERCENT, true);

	const [lastShownWarningTs, setLastShownWarningTs] = useImmer<
		Record<string, number>
	>({});

	useEffect(() => {
		if (warnings.length === 0) return;

		const now = Date.now();

		// Fire notifications for warnings that pass rate limiting
		warnings.forEach((warning) => {
			const key =
				warning.type === 'cross'
					? `warning_cross_${warning.userKey}`
					: `warning_iso_${warning.userKey}_${warning.marketIndex}`;

			const shouldSkipWarning =
				lastShownWarningTs[key] &&
				lastShownWarningTs[key] + WARNING_RATE_LIMIT_MS > now;

			if (shouldSkipWarning) return;

			setLastShownWarningTs((s) => {
				s[key] = now;
			});

			const isCurrentAccount = warning.userKey === currentUserKey;
			const accountName = accountsMap[warning.userKey]?.name ?? 'Account';
			let message: string;

			if (warning.type === 'cross') {
				message = isCurrentAccount
					? `Your Cross Position's Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`
					: `Your account (${accountName})'s Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`;
			} else {
				message = isCurrentAccount
					? `${warning.marketLabel} (Isolated) Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`
					: `Your account (${accountName})'s ${warning.marketLabel} (Isolated) Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`;
			}

			notify({
				type: 'warning',
				id: key,
				message: 'LOW HEALTH WARNING',
				description: message,
				updatePrevious: true,
			});
		});
	}, [warnings, currentUserKey, accountsMap]);
};

export default useLiquidationWarningToasts;
